import React, { useState, useContext } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import {
  Box,
  Button,
  Flex,
  Form,
  RadioButton,
  RadioButtonGroup,
  RadioField,
  Text,
  TextField,
  TextInput,
  thc,
  Row,
  Col,
} from '@flightstats/airframe-react';

import { ContactPoint } from '../../types/travelerProfile';
import {
  TravelerProfileFormValues,
  TravelerChangePasswordValues,
} from '../../types/travelerProfileForm';
import { AddToast, FormErrors } from '../../types/airframe';
import { SaveProfileError } from './TravelerProfileFormHandler';

import { TravelerProfileContext } from './TravelerProfileFormHandler';
import {
  ArrangeInputs,
  InputWrapper,
  FormControls,
} from '../../components/cirium/CiriumFormUtils';
import { Clock, ContactPointsAlertType } from './TravelerProfileConstants';
import { ContactPointsForm } from './ContactPointsForm';
import { ChangePasswordModal } from './changePassword/ChangePasswordModal';
import { ChangePasswordForm } from './changePassword/ChangePasswordForm';
import { ChangePasswordSuccessModal } from './changePassword/ChangePasswordSuccessModal';
import { ResetPasswordModal } from './resetPassword/ResetPasswordModal';
import { FilterLabeledInputRowWrapper } from '../../components/forms/Input';
import { ReactChildren } from '@flightstats/airframe-react/lib/react/components/Primitives';

export type TravelerProfileFormProps = {
  travelerKey?: string;
  email: string;
  disabled: boolean;
  passwordValidation?: (password: string) => string | boolean;
  changePasswordDisabled?: boolean;
  onSubmit: (
    errors: FormErrors,
    values: TravelerProfileFormValues,
    alertTypes: Array<keyof typeof ContactPointsAlertType>,
  ) => void;
  onTestAlert: (addToast: AddToast, contactPoint: ContactPoint) => void;
  testAlertSent: boolean;
  onCloseTestAlertSent: () => void;
  testAlertSentContact?: string;
  onChangePasswordSubmit?: (
    errors: FormErrors,
    values: TravelerChangePasswordValues,
    addToast: AddToast,
  ) => void;
  onResetPasswordSubmit?: (email: string, addToast: AddToast) => void;
  passwordChanged?: boolean;
  passwordReset?: boolean;
  passwordChangedConfirmation?: () => void;
  passwordResetConfirmation?: () => void;
  hasFormError: boolean;
  supportedSMSCountryCodes: Array<string>;
  testAlertError: Array<string>;
  saveProfilePhoneError?: SaveProfileError;
  setSaveProfilePhoneError: (error: SaveProfileError) => void;
};

const ButtonWrapper = thc(Flex, {
  alignItems: 'center',
  height: '100%',
  m: {
    xs: '10px 0px 35px 0px',
    md: '0px',
  },
});

export const TravelerProfileForm: React.FC<TravelerProfileFormProps> = ({
  travelerKey,
  email,
  disabled,
  passwordValidation,
  changePasswordDisabled = false,
  onSubmit,
  onTestAlert,
  testAlertSent,
  onCloseTestAlertSent,
  testAlertSentContact,
  onChangePasswordSubmit,
  onResetPasswordSubmit,
  passwordChanged = false,
  passwordReset = false,
  passwordChangedConfirmation,
  passwordResetConfirmation,
  hasFormError,
  supportedSMSCountryCodes,
  testAlertError,
  saveProfilePhoneError,
  setSaveProfilePhoneError,
}) => {
  const { travelerProfile, isAgent, addToast } = useContext(TravelerProfileContext);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [emailInput, updateEmailInput] = useState(email);

  const changePasswordCardTitle = 'New Password';
  const isDirtyEmail = emailInput !== email;

  const saveDisabled = !!(
    disabled ||
    (saveProfilePhoneError && Object.keys(saveProfilePhoneError).length > 0)
  );

  return (
    <>
      {travelerProfile && (
        <Form
        className='fs-exclude'
          onSubmit={(errors: FormErrors, values: TravelerProfileFormValues) => {
            onSubmit(
              errors,
              values,
              travelerProfile.contactPoints.map(
                (contactPoint: ContactPoint) => contactPoint.alertType as keyof typeof ContactPointsAlertType,
              ),
            );
          }}
        data-fs-exclude >
          {travelerKey && (          
              <FilterLabeledInputRowWrapper
                elementId="travelerKey"
                  label="Traveler Key"
              >
              {travelerKey}
              </FilterLabeledInputRowWrapper>           
          )}
          <ArrangeInputs>
            <InputWrapper>
              <TextField required>
                
                  <TextInput
                    disabled={disabled}
                    id="firstName"
                    label="First Name"
                      value={travelerProfile.firstName}
                  />
                
              </TextField>
            </InputWrapper>
            <InputWrapper>
              <TextField required>
                
                  <TextInput
                    disabled={disabled}
                    id="lastName"
                    label="Last Name"
                      value={travelerProfile.lastName}
                  />
                
              </TextField>
            </InputWrapper>
          </ArrangeInputs>
          <ArrangeInputs>
            <InputWrapper>
              <TextField required>               
                  <TextInput
                    disabled={disabled}
                    type="email"
                    id="email"
                    label="Email for Password Reset"
                      value={emailInput}
                      onChange={(e) => {
                        updateEmailInput(e.target.value);
                      }}
                  />
              </TextField>
            </InputWrapper>
            {isAgent && onResetPasswordSubmit ? (
              <InputWrapper>
                <ButtonWrapper>
                  <Tippy
                    onShow={() => { if (isDirtyEmail) return; }}
                    content={<span>Save profile to enable reset password</span>}
                  >
                    <Box style={{ paddingBottom: '12px' }}>
                      {/* this padding-bottom val here is to nudge the RESET "PASSWORD" button up a bit*/}
                      <Button
                        disabled={disabled || isDirtyEmail}
                        onClick={() => {
                          if (addToast) {
                            onResetPasswordSubmit(email, addToast);
                          }
                        }}
                      >
                        Reset Password
                      </Button>
                    </Box>
                  </Tippy>
                </ButtonWrapper>
              </InputWrapper>
            ) : (
              <InputWrapper>
                <ButtonWrapper>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      setShowChangePasswordModal(true);
                    }}
                  >
                    Change Password
                  </Button>
                </ButtonWrapper>
              </InputWrapper>
            )}
          </ArrangeInputs>
          <Row>
            <Col>
              <RadioField pb="0px">
                <RadioButtonGroup
                  id="preferredClock"
                  name="clock"
                  title="Preferred Clock"
                >
                  <RadioButton
                    disabled={disabled}
                    id={Clock.TWELVE_HOUR}
                    name="12 hour"
                    checked={travelerProfile.clock === Clock.TWELVE_HOUR}
                  >
                    {<Text className='traveler-form-clock-text'>a.m./p.m. (e.g. 6:30 p.m.)</Text> as ReactChildren}
                  </RadioButton>
                  <RadioButton
                    disabled={disabled}
                    id={Clock.TWENTY_FOUR_HOUR}
                    name="24 hour"
                    checked={travelerProfile.clock === Clock.TWENTY_FOUR_HOUR}
                  >
                    {<Text className='traveler-form-clock-text'>24 Hour (e.g. 18:30)</Text> as ReactChildren}
                  </RadioButton>
                </RadioButtonGroup>
              </RadioField>
            </Col>
          </Row>
          <ContactPointsForm
            saveProfilePhoneError={saveProfilePhoneError}
            setSaveProfilePhoneError={setSaveProfilePhoneError}
            disabled={disabled}
            contactPoints={travelerProfile.contactPoints}
            onTestAlert={onTestAlert}
            testAlertSent={testAlertSent}
            onCloseTestAlertSent={onCloseTestAlertSent}
            testAlertSentContact={testAlertSentContact}
            supportedSMSCountryCodes={supportedSMSCountryCodes}
            hasFormError={hasFormError}
            testAlertError={testAlertError}
          />
          <FormControls disabled={saveDisabled} label="Save" />
        </Form>
      )}
      {passwordValidation &&
        onChangePasswordSubmit &&
        passwordChangedConfirmation && (
          <>
            <ChangePasswordModal
              showModal={showChangePasswordModal && !passwordChanged}
              closeModal={() => {
                setShowChangePasswordModal(false);
              }}
              title={changePasswordCardTitle}
            >
              {addToast && (
                <ChangePasswordForm
                  passwordValidation={passwordValidation}
                  disabled={changePasswordDisabled}
                  onSubmit={onChangePasswordSubmit}
                  addToast={addToast}
                />
              )}
            </ChangePasswordModal>
            <ChangePasswordSuccessModal
              showModal={passwordChanged}
              confirmSuccess={passwordChangedConfirmation}
              title={changePasswordCardTitle}
            />
          </>
        )}

      {passwordResetConfirmation && (
        <ResetPasswordModal
          showModal={passwordReset}
          confirmSuccess={passwordResetConfirmation}
          email={email}
        />
      )}
    </>
  );
};